/**
 * ProjetosPage — Portal Cliente, Projetos 2.0 F3.1.
 *
 * O Gestor acompanha o andamento dos seus projetos: etapas (em árvore), datas
 * e % de execução. **SEM valores** — o backend (`GET /api/v1/cliente/projetos`)
 * já entrega só campos não-financeiros, filtrados por X-Client-Context +
 * feature_key `ver_projetos` (gated pela flag PORTAL_CLIENTE_PROJETOS_ENABLED).
 *
 * Read-only: sem ações. Calendário/Gantt fica pra rodada futura.
 */
import { Alert, Badge, EmptyState } from '@/components/ui';
import { PortalPage } from '@/components/cliente/PortalPage';
import { useCliente } from '@/contexts/ClienteContext';
import {
  useProjetosCliente,
  type ProjetoCliente,
  type ProjetoEtapaCliente,
} from '@/lib/cliente-portal-store';
import { mensagemErro } from '@/lib/mensagem-publica';

import { formatarData } from '@/lib/format-date';
export function ProjetosPage() {
  const { clienteAtivo } = useCliente();
  const { data, isLoading, isError, error } = useProjetosCliente();

  const projetos = data?.items ?? [];

  return (
    <PortalPage
      testId="projetos-page"
      title="Projetos"
      subtitle={
        clienteAtivo
          ? `Como está andando cada obra em ${clienteAtivo.nome} — etapas, prazos e o quanto já foi concluído.`
          : 'Etapas, prazos e andamento das obras.'
      }
    >
      {isLoading ? (
        <p className="text-sm text-ink-2 dark:text-ink-dark-2" aria-busy="true">
          Carregando…
        </p>
      ) : isError ? (
        <Alert variant="danger">
          {mensagemErro(error, 'Não conseguimos carregar seus projetos agora. Tente atualizar a página.')}
        </Alert>
      ) : projetos.length === 0 ? (
        <EmptyState
          title="Nenhum projeto por aqui ainda"
          description="Quando a IGM iniciar uma obra para este condomínio, o andamento aparece nesta tela — etapa por etapa."
        />
      ) : (
        <ul className="space-y-4">
          {projetos.map((p) => (
            <ProjetoCard key={p.id} projeto={p} />
          ))}
        </ul>
      )}
    </PortalPage>
  );
}

function ProjetoCard({ projeto: p }: { projeto: ProjetoCliente }) {
  return (
    <li
      className="block p-4 rounded border border-line dark:border-line-dark bg-paper-surface dark:bg-paper-dark-surface"
      data-testid={`projeto-card-${p.id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-ink-1 dark:text-ink-dark-1">{p.nome}</div>
          <div className="text-xs text-ink-3 dark:text-ink-dark-3 mt-0.5">
            {p.tipoNome && <span>{p.tipoNome} · </span>}
            {periodoLabel(p.dataInicio, p.dataFim)}
          </div>
        </div>
        <Badge variant={statusVariant(p.status)} size="sm">
          {statusLabel(p.status)}
        </Badge>
      </div>

      <ProgressBar percentual={p.percentual} />

      {p.etapas.length > 0 && (
        <ul className="mt-3 space-y-2" data-testid={`projeto-etapas-${p.id}`}>
          {p.etapas.map((e) => (
            <EtapaRow key={e.id} etapa={e} depth={depthOf(e, p.etapas)} />
          ))}
        </ul>
      )}
    </li>
  );
}

function EtapaRow({ etapa: e, depth }: { etapa: ProjetoEtapaCliente; depth: number }) {
  return (
    <li
      className="text-sm"
      style={{ paddingLeft: `${depth * 16}px` }}
      data-testid={`etapa-row-${e.id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-ink-2 dark:text-ink-dark-2 truncate">
          {depth > 0 && <span className="text-ink-3 dark:text-ink-dark-3">↳ </span>}
          {e.nome}
        </span>
        <span className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-ink-3 dark:text-ink-dark-3 tabular-nums">{e.percentual}%</span>
          <Badge variant={statusVariant(e.status)} size="sm">
            {statusLabel(e.status)}
          </Badge>
        </span>
      </div>
      <div className="mt-1">
        <ProgressBar percentual={e.percentual} thin />
      </div>
      {(e.dataInicio || e.dataFim) && (
        <div className="text-xs text-ink-3 dark:text-ink-dark-3 mt-0.5">
          {periodoLabel(e.dataInicio, e.dataFim)}
        </div>
      )}
    </li>
  );
}

function ProgressBar({ percentual, thin = false }: { percentual: number; thin?: boolean }) {
  const pct = Math.max(0, Math.min(100, Math.round(percentual)));
  return (
    <div
      className={`w-full rounded-full bg-line-soft dark:bg-paper-dark-warm ${thin ? 'h-1.5' : 'h-2'}`}
      role="progressbar"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className="h-full rounded-full bg-red transition-all"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

// ─── helpers ─────────────────────────────────────────────────────────────

function depthOf(etapa: ProjetoEtapaCliente, all: ProjetoEtapaCliente[]): number {
  const byId = new Map(all.map((e) => [e.id, e]));
  let depth = 0;
  let cur: ProjetoEtapaCliente | undefined = etapa;
  // Guard contra ciclos: no máximo a profundidade do array.
  while (cur?.parentEtapaId && depth < all.length) {
    cur = byId.get(cur.parentEtapaId);
    depth += 1;
  }
  return depth;
}

function statusVariant(status: string | null): 'success' | 'info' | 'warning' | 'neutral' | 'danger' {
  switch (status) {
    case 'concluido':
    case 'concluida':
    case 'finalizado':
      return 'success';
    case 'em_andamento':
      return 'info';
    case 'bloqueado':
    case 'cancelado':
      return 'danger';
    case 'pendente':
    case null:
      return 'neutral';
    default:
      return 'warning';
  }
}

function statusLabel(status: string | null): string {
  const map: Record<string, string> = {
    pendente: 'Pendente',
    em_andamento: 'Em andamento',
    concluido: 'Concluído',
    concluida: 'Concluída',
    finalizado: 'Concluído',
    bloqueado: 'Bloqueado',
    cancelado: 'Cancelado',
  };
  // Nunca devolve o código cru: status novo no banco vira "Em andamento" na
  // tela do cliente em vez de um slug tipo "aguardando_material".
  if (!status) return 'Pendente';
  return map[status] ?? 'Em andamento';
}

function fmtDate(iso: string | null): string | null {
  if (!iso) return null;
  try {
    return formatarData(iso, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function periodoLabel(inicio: string | null, fim: string | null): string {
  const i = fmtDate(inicio);
  const f = fmtDate(fim);
  if (i && f) return `${i} → ${f}`;
  if (i) return `Início ${i}`;
  if (f) return `Entrega ${f}`;
  return 'Sem datas definidas';
}
